import React, { useCallback, useEffect, useState } from "react";

import * as api from "../api";
import AuditList from "../components/AuditList";
import Button from "../components/Button";
import AccountModal from "../components/modals/Account";
import Pill from "../components/Pill";
import type { PillTone } from "../logic";
import type { PageProps } from "./types";

const SYSTEM_ROLES: api.SystemRole[] = ["owner", "admin", "manager", "member"];
const PLATFORM_ROLES: api.PlatformRole[] = ["NONE", "STAFF", "SUPERADMIN"];

const th: React.CSSProperties = { textAlign: "left", fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 400, padding: "8px 10px", borderBottom: "1px solid var(--border-hairline)" };
const td: React.CSSProperties = { padding: "11px 10px", borderBottom: "1px solid var(--border-hairline)" };
const h3: React.CSSProperties = { margin: "24px 0 10px", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)" };
const smallInput: React.CSSProperties = { fontFamily: "var(--font-sans)", fontSize: 13.5, color: "var(--ink-900)", background: "#fff", border: "1px solid var(--border-strong)", borderRadius: 8, padding: "9px 12px", outline: "none" };

function statusTone(s: string): PillTone {
  return s === "active" ? "ok" : s === "suspended" ? "warn" : "danger";
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }}>
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function PlatformCell({ role }: { role: api.PlatformRole }) {
  if (role === "NONE") return <span style={{ color: "var(--text-faint)", fontFamily: "var(--font-mono)", fontSize: 12 }}>—</span>;
  return <Pill tone={role === "SUPERADMIN" ? "violet" : "sky"}>{role}</Pill>;
}

export default function Users({ effectiveRole, showToast }: PageProps) {
  const isSuper = effectiveRole === "SUPERADMIN";
  const [q, setQ] = useState("");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [rows, setRows] = useState<api.UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.searchUsers(q, includeDisabled);
      setRows(res.users);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  }, [q, includeDisabled]);

  useEffect(() => {
    const t = setTimeout(runSearch, 200);
    return () => clearTimeout(t);
  }, [runSearch]);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.25fr 1fr", height: "100%" }}>
      <section style={{ padding: "24px 28px", overflow: "auto", borderRight: "1px solid var(--border-hairline)" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <SearchIcon />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users by name or email…" style={{ width: "100%", fontFamily: "var(--font-sans)", fontSize: 14, color: "var(--ink-900)", background: "#fff", border: "1px solid var(--border-strong)", borderRadius: 8, padding: "11px 13px 11px 38px", outline: "none" }} />
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--text-muted)", cursor: "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={includeDisabled} onChange={(e) => setIncludeDisabled(e.target.checked)} style={{ width: 15, height: 15, accentColor: "var(--violet-500)", cursor: "pointer" }} />show disabled
          </label>
        </div>
        <p style={{ margin: "0 0 8px", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-faint)" }}>
          {loading ? "loading…" : `${total} result${total === 1 ? "" : "s"}`}
        </p>
        {error && <p style={{ color: "var(--danger-500)", fontSize: 13 }}>{error}</p>}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={th}>User</th><th style={th}>Status</th><th style={th}>Platform</th>
            <th style={{ ...th, textAlign: "right" }}>Orgs</th>
          </tr></thead>
          <tbody>
            {rows.map((u) => {
              const sel = u.id === selected;
              return (
                <tr key={u.id} onClick={() => setSelected(u.id)} style={{ cursor: "pointer", background: sel ? "var(--surface-sunken)" : "transparent", boxShadow: sel ? "inset 3px 0 0 var(--violet-500)" : "none" }}>
                  <td style={td}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-900)" }}>{u.name || u.id}</div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{u.email}</div>
                  </td>
                  <td style={td}><Pill tone={statusTone(u.status)}>{u.status}</Pill></td>
                  <td style={td}><PlatformCell role={u.platform_role} /></td>
                  <td style={{ ...td, textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--ink-700)" }}>{u.org_count}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && rows.length === 0 && <p style={{ padding: "24px 10px", color: "var(--text-muted)", fontSize: 13.5 }}>No users match “{q}”.</p>}
      </section>

      <section style={{ overflow: "auto", background: "#fff" }}>
        {selected ? (
          <UserDetail key={selected} userId={selected} isSuper={isSuper} showToast={showToast} onChanged={runSearch} />
        ) : (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-faint)", fontSize: 14 }}>Select a user to view details.</div>
        )}
      </section>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)" }}>{label}</div>
      <div style={{ fontSize: 14, color: "var(--ink-900)", marginTop: 4 }}>{children}</div>
    </div>
  );
}

function UserDetail({
  userId,
  isSuper,
  showToast,
  onChanged,
}: {
  userId: string;
  isSuper: boolean;
  showToast: (m: string) => void;
  onChanged: () => void;
}) {
  const [user, setUser] = useState<api.UserDetail | null>(null);
  const [audit, setAudit] = useState<api.AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<null | "suspend" | "disable">(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setUser(await api.getUser(userId));
    } catch (e: any) {
      setError(e?.message || "Failed to load user");
    }
    try {
      const a = await api.listAudit({ targetUserId: userId });
      setAudit(a.entries.slice(0, 6));
    } catch {
      setAudit([]);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (!user) return <div style={{ padding: 28 }}>{error ? <p style={{ color: "var(--danger-500)" }}>{error}</p> : <p style={{ color: "var(--text-muted)" }}>Loading…</p>}</div>;

  const act = async (fn: () => Promise<unknown>, msg: string) => {
    setError(null);
    try {
      await fn();
      await load();
      onChanged();
      showToast(msg);
    } catch (e: any) {
      setError(e?.message || "Action failed");
    }
  };

  const accountAction = async (call: (r: string) => Promise<any>, verb: string, reason: string) => {
    const res = await call(reason);
    await load();
    onChanged();
    const orphans: string[] = res?.orphaned_orgs || [];
    showToast(orphans.length ? `${verb} — ⚠ now-ownerless org(s): ${orphans.join(", ")}. Transfer ownership.` : verb);
  };

  return (
    <div style={{ padding: "26px 28px 40px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 22, letterSpacing: "-0.02em", color: "var(--ink-900)" }}>{user.name || user.id}</h2>
          <p style={{ margin: "5px 0 0", fontSize: 13, color: "var(--text-muted)" }}>{user.email || "no email"} · <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{user.id}</span></p>
        </div>
        <Pill tone={statusTone(user.status)}>{user.status}</Pill>
      </div>

      <div style={{ display: "flex", gap: 28, margin: "20px 0 4px", padding: "16px 0", borderTop: "1px solid var(--border-hairline)", borderBottom: "1px solid var(--border-hairline)" }}>
        <Fact label="Created">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</Fact>
        <Fact label="Recent calls"><span style={{ fontFamily: "var(--font-mono)" }}>{user.recent_call_count}</span></Fact>
        <Fact label="Platform role">{user.platform_role}</Fact>
      </div>

      {error && <p style={{ color: "var(--danger-500)", fontSize: 13, marginTop: 8 }}>{error}</p>}

      <h3 style={h3}>Org memberships</h3>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {user.memberships.map((m) => (
            <tr key={m.org_id}>
              <td style={{ padding: "10px 8px 10px 0", borderBottom: "1px solid var(--border-hairline)" }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-900)" }}>{m.org_name || m.org_id}{m.is_personal && <span style={{ color: "var(--text-muted)" }}> · personal</span>}</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{m.has_verified_caller_id ? "caller ID verified" : "no verified caller ID"}</div>
              </td>
              <td style={{ padding: "10px 8px", borderBottom: "1px solid var(--border-hairline)", width: 140 }}>
                <select
                  value={m.system_role}
                  disabled={!isSuper}
                  onChange={(e) => act(() => api.changeRole(userId, m.org_id, e.target.value as api.SystemRole), `Role → ${e.target.value} @ ${m.org_name || m.org_id}`)}
                  style={{ ...smallInput, width: "100%", fontSize: 13, padding: "7px 10px", cursor: isSuper ? "pointer" : "not-allowed", color: isSuper ? "var(--ink-900)" : "var(--text-muted)", background: isSuper ? "#fff" : "var(--surface-sunken)" }}
                >
                  {SYSTEM_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </td>
              <td style={{ padding: "10px 0 10px 8px", borderBottom: "1px solid var(--border-hairline)", width: 70, textAlign: "right" }}>
                {isSuper && (
                  <button type="button" onClick={() => act(() => api.removeMembership(userId, m.org_id), `Removed from ${m.org_name || m.org_id}`)} style={{ background: "none", border: "none", color: "var(--danger-500)", fontFamily: "var(--font-sans)", fontWeight: 600, fontSize: 12.5, cursor: "pointer", padding: "5px 8px", borderRadius: 6 }}>remove</button>
                )}
              </td>
            </tr>
          ))}
          {user.memberships.length === 0 && <tr><td style={{ padding: "12px 0", color: "var(--text-muted)", fontSize: 13 }}>No memberships.</td></tr>}
        </tbody>
      </table>
      {isSuper && <AddMembership userId={userId} existingOrgIds={user.memberships.map((m) => m.org_id)} onDone={(name) => act(async () => {}, `Added to ${name}`)} />}

      <h3 style={h3}>Account</h3>
      {isSuper ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {user.status === "suspended" && <Button variant="ghost" size="sm" onClick={() => act(() => api.unsuspendUser(userId), "User unsuspended.")}>Unsuspend</Button>}
          {user.status === "active" && <Button variant="ghost" size="sm" onClick={() => setModal("suspend")}>Suspend…</Button>}
          {user.status === "disabled" && <Button variant="ghost" size="sm" onClick={() => act(() => api.restoreUser(userId), "User restored.")}>Restore</Button>}
          {user.status !== "disabled" && (
            <button type="button" onClick={() => setModal("disable")} style={{ display: "inline-flex", alignItems: "center", fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 12.5, color: "var(--danger-500)", background: "var(--danger-100)", border: "1px solid var(--danger-200)", borderRadius: 8, padding: "8px 14px", cursor: "pointer" }}>Disable…</button>
          )}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: "var(--text-muted)", background: "var(--surface-sunken)", border: "1px solid var(--border-hairline)", borderRadius: 10, padding: "12px 14px", margin: 0 }}>
          Read-only as <b style={{ color: "var(--ink-700)" }}>staff</b>. Account actions and platform-access changes require superadmin.
        </p>
      )}

      {isSuper && (
        <>
          <h3 style={h3}>Platform access</h3>
          <select value={user.platform_role} onChange={(e) => act(() => api.setPlatformRole(userId, e.target.value as api.PlatformRole), `Platform role → ${e.target.value}.`)} style={{ ...smallInput, cursor: "pointer" }}>
            {PLATFORM_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </>
      )}

      <h3 style={h3}>Recent audit</h3>
      <AuditList entries={audit} compact />

      {modal && (
        <AccountModal
          verb={modal}
          userName={user.name || user.id}
          onCancel={() => setModal(null)}
          onConfirm={async (reason) => {
            const call = modal === "suspend" ? (r: string) => api.suspendUser(userId, r) : (r: string) => api.disableUser(userId, r);
            await accountAction(call, modal === "suspend" ? "User suspended." : "User disabled.", reason);
            setModal(null);
          }}
        />
      )}
    </div>
  );
}

function AddMembership({ userId, existingOrgIds, onDone }: { userId: string; existingOrgIds: string[]; onDone: (orgName: string) => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<api.OrgRow[]>([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<api.OrgRow | null>(null);
  const [role, setRole] = useState<api.SystemRole>("member");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selected && query === (selected.name || selected.id)) return;
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.searchOrgs(query);
        if (!active) return;
        setResults(res.orgs.filter((o) => !existingOrgIds.includes(o.id)));
      } catch {
        if (active) setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 200);
    return () => { active = false; clearTimeout(t); };
  }, [query, selected, existingOrgIds]);

  const add = async () => {
    if (!selected) { setError("Pick an organization first."); return; }
    setError(null);
    try {
      await api.addMembership(userId, selected.id, role);
      const name = selected.name || selected.id;
      setQuery("");
      setSelected(null);
      setResults([]);
      setRole("member");
      onDone(name);
    } catch (e: any) {
      setError(e?.message || "Failed to add membership");
    }
  };

  return (
    <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
      <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
        <input
          placeholder="search organization to add…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(null); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          style={{ ...smallInput, width: "100%" }}
        />
        {open && (
          <ul style={{ listStyle: "none", margin: "4px 0 0", padding: 4, position: "absolute", zIndex: 5, left: 0, right: 0, background: "#fff", border: "1px solid var(--border-hairline)", borderRadius: 8, boxShadow: "var(--elevation-md)", maxHeight: 220, overflow: "auto" }}>
            {loading && <li style={{ color: "var(--text-muted)", padding: "6px 8px", fontSize: 13 }}>searching…</li>}
            {!loading && results.map((o) => (
              <li key={o.id} onMouseDown={() => { setSelected(o); setQuery(o.name || o.id); setOpen(false); }} style={{ padding: "6px 8px", cursor: "pointer", borderRadius: 6, fontSize: 13 }}>
                <span style={{ color: "var(--ink-900)" }}>{o.name || o.id}</span>{" "}
                <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: 11 }}>{o.id}</span>
              </li>
            ))}
            {!loading && results.length === 0 && <li style={{ color: "var(--text-muted)", padding: "6px 8px", fontSize: 13 }}>no matches</li>}
          </ul>
        )}
      </div>
      <select value={role} onChange={(e) => setRole(e.target.value as api.SystemRole)} style={{ ...smallInput, cursor: "pointer" }}>
        {SYSTEM_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <Button variant="secondary" size="sm" onClick={add} disabled={!selected}>Add org</Button>
      {error && <span style={{ color: "var(--danger-500)", fontSize: 12 }}>{error}</span>}
    </div>
  );
}
